import { BinaryWriter } from "../binary.js";
import { ClientOp, ServerOp } from "../opcodes.js";
/**
 * Troca segura entre jogadores, em duas etapas (PRD §5).
 *
 * As duas etapas não são burocracia: são a única defesa contra o golpe clássico do gênero,
 * em que o vigarista troca a oferta no instante entre o outro ler e clicar. Com confirmação
 * em duas etapas, **qualquer** mudança numa das ofertas derruba as duas confirmações — quem
 * já tinha aceitado precisa aceitar de novo, e por isso o que ele confirma é sempre o que
 * ele leu.
 *
 * O que a torna "segura" no sentido do PRD tem uma segunda metade, do lado do servidor: a
 * troca só termina quando os dois inventários são gravados **na mesma transação**. Sem
 * isso, uma queda entre uma escrita e outra duplicaria um item ou o apagaria, e nenhuma das
 * duas é recuperável depois.
 */
export const TradeAction = {
    /** Convida o alvo para trocar. */
    Request: 0,
    /** Põe um item na própria oferta. */
    Offer: 1,
    /** Tira um item da própria oferta. */
    Withdraw: 2,
    /** Confirma o que está na mesa. */
    Accept: 3,
    /** Desiste. Vale para o convite e para a troca já aberta. */
    Cancel: 4,
};
export function encodeTradeRequest(packet, writer = new BinaryWriter(16)) {
    writer
        .u8(ClientOp.Trade)
        .u8(packet.action)
        .u32(packet.targetId)
        .u32(packet.container)
        .u8(packet.slot)
        .u16(packet.count);
    return writer;
}
export function decodeTradeRequest(reader) {
    const rawAction = reader.u8();
    const action = rawAction >= TradeAction.Request && rawAction <= TradeAction.Cancel
        ? rawAction
        : TradeAction.Cancel;
    return {
        action,
        targetId: reader.u32(),
        container: reader.u32(),
        slot: reader.u8(),
        count: reader.u16(),
    };
}
export const TradeState = {
    /** Nenhuma troca em curso. Fecha a janela. */
    Idle: 0,
    /** Convite enviado, esperando o outro aceitar. */
    Pending: 1,
    /** Mesa aberta: os dois põem itens e confirmam. */
    Open: 2,
};
export function encodeTrade(packet, writer = new BinaryWriter(128)) {
    writer
        .u8(ServerOp.Trade)
        .u8(packet.state)
        .u32(packet.partnerId)
        .str8(packet.partnerName)
        .u8(packet.mineAccepted ? 1 : 0)
        .u8(packet.theirsAccepted ? 1 : 0);
    writeItems(writer, packet.mine);
    writeItems(writer, packet.theirs);
    return writer;
}
export function decodeTrade(reader) {
    const rawState = reader.u8();
    const state = rawState === TradeState.Pending || rawState === TradeState.Open
        ? rawState
        : TradeState.Idle;
    return {
        state,
        partnerId: reader.u32(),
        partnerName: reader.str8(),
        mineAccepted: reader.u8() !== 0,
        theirsAccepted: reader.u8() !== 0,
        mine: readItems(reader),
        theirs: readItems(reader),
    };
}
function writeItems(writer, items) {
    writer.u8(items.length);
    for (const item of items)
        writer.u16(item.typeId).u16(item.count);
}
function readItems(reader) {
    const count = reader.u8();
    const items = new Array(count);
    for (let i = 0; i < count; i++) {
        items[i] = { typeId: reader.u16(), count: reader.u16() };
    }
    return items;
}
/**
 * Teto de linhas numa oferta.
 *
 * Existe porque a mesa é lida antes de ser aceita: uma oferta de duzentas linhas não é
 * generosidade, é uma forma de esconder o que foi trocado atrás de uma barra de rolagem.
 */
export const MAX_TRADE_ITEMS = 8;
//# sourceMappingURL=trade.js.map